// Interfaces
export interface WidgetGraphType {
    name: string;
    value: string;
    type: string;
    options: string;
    icon: string;
}

// Variables
// The widget types of the editor and of the catalog, in this order.
export const WIDGET_GRAPH_TYPES: readonly WidgetGraphType[] = [
    { name: "Text Block", value: "text", type: "block", options: "type3", icon: "text_fields" },
    { name: "Stacked Bar", value: "barStacked", type: "chart", options: "type1", icon: "bar_chart" },
    { name: "Grouped Bar", value: "barGrouped", type: "chart", options: "type1", icon: "equalizer" },
    { name: "Stacked Column", value: "columnStacked", type: "chart", options: "type1", icon: "insert_chart" },
    { name: "Grouped Column", value: "columnGrouped", type: "chart", options: "type1", icon: "insert_chart_outlined" },
    { name: "Discrete Line", value: "discreteLine", type: "chart", options: "type1", icon: "show_chart" },
    { name: "Continious Line", value: "continiousLine", type: "chart", options: "type1", icon: "timeline" },
    { name: "Discrete Area", value: "discreteArea", type: "chart", options: "type1", icon: "area_chart" },
    { name: "Continious Area", value: "continiousArea", type: "chart", options: "type1", icon: "stacked_line_chart" },
    { name: "Stacked Mix", value: "mixStacked", type: "chart", options: "type2", icon: "candlestick_chart" },
    { name: "Grouped Mix", value: "mixGrouped", type: "chart", options: "type2", icon: "multiline_chart" },
    { name: "Pie Chart", value: "pie", type: "chart", options: "type6", icon: "pie_chart" },
    { name: "Doughnut", value: "doughnut", type: "chart", options: "type6", icon: "donut_small" },
    { name: "Grid Scatter", value: "gridScatter", type: "chart", options: "type4", icon: "scatter_plot" },
    { name: "Heatmap Maps", value: "geoHeatmap", type: "map", options: "type5", icon: "map" },
    { name: "Scatter Maps", value: "geoScatter", type: "map", options: "type5", icon: "place" },
    { name: "Scatter 2D/3D", value: "geoScatter3D", type: "forge", options: "type5", icon: "3d_rotation" },
    { name: "Planning 3D", value: "planning3D", type: "planning3D", options: "type5", icon: "view_in_ar" },
    { name: "Image", value: "image", type: "image", options: "type9", icon: "image" },
    { name: "Table", value: "table", type: "table", options: "type7", icon: "table_chart" },
    { name: "Video", value: "video", type: "video", options: "type8", icon: "videocam" },
    { name: "Weather", value: "weather", type: "weather", options: "type8", icon: "wb_sunny" },
    { name: "editor.graph_distribution", value: "barDistribution", type: "chart", options: "type10", icon: "percent" },
    { name: "editor.graph_heatmap", value: "heatmap", type: "heatmap", options: "type11", icon: "grid_on" },
    { name: "editor.graph_project_map", value: "projectMap", type: "projectMap", options: "type12", icon: "public" },
    { name: "editor.graph_insight", value: "insight", type: "insight", options: "type1", icon: "insights" }
];

// Widget types that make sense over several projects (no 3D model, planning, image or video of one project).
export const SCOPE_WIDGET_TYPES: readonly string[] = ['chart', 'block', 'table', 'map', 'heatmap', 'projectMap', 'insight'];
// Types that only exist in a synthesis (their positions come from the company projects).
export const SCOPE_ONLY_WIDGET_TYPES: readonly string[] = ['projectMap'];

// Whether a widget type can be added in this context (a synthesis or a project dashboard).
export function widgetTypeAllowed(type: string | undefined, scoped: boolean): boolean {
    return scoped ? SCOPE_WIDGET_TYPES.includes(type ?? '') : !SCOPE_ONLY_WIDGET_TYPES.includes(type ?? '');
}

export function widgetGraphTypes(scoped: boolean): WidgetGraphType[] {
    return WIDGET_GRAPH_TYPES.filter(graphType => widgetTypeAllowed(graphType.type, scoped)).map(graphType => ({ ...graphType }));
}

// Catalog families of the widget types.
export type WidgetFamilyKey = 'chart' | 'text' | 'table' | 'part' | 'insight' | 'heatmap' | 'map' | 'media';

export interface WidgetFamily {
    key: WidgetFamilyKey;
    name: string;   // i18n key
    icon: string;
    types: string[];
}

// From the most used to the least used: the order of the catalog.
const POPULARITY: readonly string[] = [
    'columnStacked', 'discreteLine', 'text', 'table', 'pie', 'doughnut', 'barStacked', 'columnGrouped', 'continiousLine',
    'discreteArea', 'barDistribution', 'insight', 'heatmap', 'projectMap', 'barGrouped', 'continiousArea', 'image', 'weather',
    'mixStacked', 'mixGrouped', 'geoScatter', 'geoHeatmap', 'gridScatter', 'video', 'geoScatter3D', 'planning3D'
];

export const WIDGET_FAMILIES: readonly WidgetFamily[] = [
    { key: 'chart', name: 'catalog.family_chart', icon: 'bar_chart', types: ['columnStacked', 'discreteLine', 'barStacked', 'columnGrouped', 'continiousLine', 'discreteArea', 'barGrouped', 'continiousArea', 'mixStacked', 'mixGrouped', 'gridScatter'] },
    { key: 'text', name: 'catalog.family_text', icon: 'text_fields', types: ['text'] },
    { key: 'table', name: 'catalog.family_table', icon: 'table_chart', types: ['table'] },
    { key: 'part', name: 'catalog.family_part', icon: 'donut_small', types: ['pie', 'doughnut', 'barDistribution'] },
    { key: 'insight', name: 'catalog.family_insight', icon: 'insights', types: ['insight'] },
    { key: 'heatmap', name: 'catalog.family_heatmap', icon: 'grid_on', types: ['heatmap'] },
    { key: 'map', name: 'catalog.family_map', icon: 'public', types: ['projectMap', 'geoScatter', 'geoHeatmap'] },
    { key: 'media', name: 'catalog.family_media', icon: 'perm_media', types: ['image', 'weather', 'video', 'geoScatter3D', 'planning3D'] },
];

// The types of the context, the most used first.
export function popularGraphTypes(scoped: boolean): WidgetGraphType[] {
    return widgetGraphTypes(scoped).sort((a, b) => POPULARITY.indexOf(a.value) - POPULARITY.indexOf(b.value));
}
